import "@/styles/Layout.scss";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";

import getArchived from "@/scripts/getArchived";
import getConstants from "@/scripts/getConstants";
import convertTimestamp from "@/scripts/convertTimestamp";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import { ListBulletIcon } from "@radix-ui/react-icons";
import Spinner from "@/components/Spinner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

function ManageArchived() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  // Constants
  const [forms, setForms] = useState([]);
  const [grades, setGrades] = useState([]);

  // Filters
  const [showFilter, setShowFilter] = useState(true);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    grade: "all",
    form: "all",
    status: "all",
    type: "all",
  });
  const [filteredList, setFilteredList] = useState([]);

  // Define columns for the table
  const columns = [
    {
      accessorKey: "reference_id",
      header: "Reference ID",
    },
    {
      accessorKey: "company",
      header: "Company",
    },
    {
      accessorKey: "name",
      header: "Person",
    },
    {
      accessorKey: "created_at",
      header: "Date",
      cell: (info) => convertTimestamp(info.getValue()),
    },
    {
      accessorKey: "product.grade",
      header: "Grade",
    },
    {
      accessorKey: "product.form",
      header: "Form",
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => <div className="status archived">Archived</div>,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: (info) => (
        <Button
          className="px-6 rounded-full"
          onClick={() => viewProduct(info.row.original)}
        >
          View
        </Button>
      ),
    },
  ];

  // Initialize the table
  const table = useReactTable({
    columns,
    data: filteredList,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10, // Default page size
      },
    },
  });

  const viewProduct = (item) => {
    navigate("/product", { state: { data: item } });
  };

  const capitalizedStatus = (status) => {
    if (status === "processing") {
      return "Under Review";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters({ ...filters, [name]: value });
  };

  // Filter logic
  const filterProducts = () => {
    const filtered = list.filter((product) => {
      // Convert Firestore Timestamp to JavaScript Date
      const productDate = new Date(product.created_at);

      // Parse startDate and endDate
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;

      const matchDate =
        (!startDate || productDate >= startDate) &&
        (!endDate ||
          productDate <= new Date(new Date(endDate).setHours(23, 59, 59, 999)));

      const matchType =
        filters.type && filters.type !== "all"
          ? product.type === filters.type
          : true;

      const matchGrade =
        filters.grade && filters.grade !== "all"
          ? product.product.grade === filters.grade
          : true;

      const matchForm =
        filters.form && filters.form !== "all"
          ? product.product.form === filters.form
          : true;

      const matchStatus =
        filters.status === "all"
          ? true
          : filters.status === "under review"
          ? product.status === "processing"
          : product.status === filters.status;

      return matchType && matchDate && matchGrade && matchForm && matchStatus;
    });

    setFilteredList(filtered); // Update the filteredList state
  };

  const getData = async () => {
    try {
      setLoading(true);
      const fetchedList = await getArchived();

      setList(fetchedList);
      setFilteredList(fetchedList);
    } catch (error) {
      console.log("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchConstants = async () => {
    try {
      const { forms, grades } = await getConstants();
      // Set values
      setForms(forms);
      setGrades(grades);
    } catch (err) {
      console.log("Unable to fetch constants", err);
    }
  };

  useEffect(() => {
    getData(); // Fetch data once on page load
    fetchConstants();
  }, []);

  useEffect(() => {
    filterProducts();
  }, [filters]);

  if (loading || !grades || !forms) {
    return <Spinner />;
  }

  return (
    <div className="sectionWrapper mobileWrapper mobileTableWrapper">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-sm font-bold text-green ml-0.5">
            {showFilter ? "Product Filter" : "Product Form"}
          </h1>
          <div className="highlight mb-3" />
        </div>

        <Button onClick={() => setShowFilter(!showFilter)}>
          <ListBulletIcon />
          Filters
        </Button>
      </div>

      {
        /* Filters */
        showFilter && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Request Type
                </label>
                <Select
                  onValueChange={(value) => handleFilterChange("type", value)}
                  value={filters.type}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.type) || "Select Type"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Type</SelectItem>
                    <SelectItem value="archived_buyer_request">
                      Buyer Request
                    </SelectItem>
                    <SelectItem value="archived_seller_request">
                      Seller Request
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Grade Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Grade</label>
                <Select
                  onValueChange={(value) => handleFilterChange("grade", value)}
                  value={filters.grade}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.grade) || "Select Grade"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Grades</SelectItem>
                    {/* Map */}
                    {grades.map((grade, i) => (
                      <SelectItem key={i} value={grade}>
                        {grade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Form Filter */}
              <div>
                <label className="block text-sm font-medium mb-1">Form</label>
                <Select
                  onValueChange={(value) => handleFilterChange("form", value)}
                  value={filters.form}
                >
                  <SelectTrigger className="w-full">
                    <span>
                      {capitalizedStatus(filters.form) || "Select Form"}
                    </span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Forms</SelectItem>
                    {/* Map */}
                    {forms.map((form, i) => (
                      <SelectItem key={i} value={form}>
                        {form}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value)
                  }
                />
              </div>
            </div>

            <div>
              <h1 className="text-sm font-bold text-green ml-0.5">
                Product Form
              </h1>
              <div className="highlight mb-3" />
            </div>
          </>
        )
      }

      {/* Table */}
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </span>
          <Select
            value={table.getState().pagination.pageSize.toString()}
            onValueChange={(value) => table.setPageSize(Number(value))}
          >
            <SelectTrigger className="w-[80px]">
              <span className="text-sm">
                {table.getState().pagination.pageSize}
              </span>
            </SelectTrigger>
            <SelectContent className="!w-[80px]">
              {[10, 25, 50].map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  Show {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

export default ManageArchived;
